import FeatureBento from '../components/stageshow/FeatureBento';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/common/SEO';
import { HowToSchema } from '../components/common/StructuredData';
import NewsletterCapture from '../components/marketing/NewsletterCapture';
import './landing/landing.css';

const blogSnippets = [
  {
    id: 'nexus-thresholds',
    title: 'What is Economic Nexus? Why It Matters for Your Business',
    snippet: 'Learn how economic nexus laws impact your sales tax obligations in different states.'
  },
  {
    id: 'food-taxability',
    title: 'Is Food Taxable? Surprising State-by-State Differences',
    snippet: 'Food taxability varies widely. Some states tax groceries, others don\'t, and some tax only certain foods.'
  },
  {
    id: 'audit-prep',
    title: 'Top 5 Sales Tax Audit Surprises (and How to Stay Ready)',
    snippet: 'Reduce audit anxiety with proactive reconciliations, variance monitoring, and documentation tips.'
  }
];

/* Pricing: copied verbatim from the previous PricingTable props. Do not change without the owner. */
const tiers = [
  {
    id: 'monitor',
    title: 'Monitor',
    price: 'Free',
    cadence: '',
    highlights: [
      'Nexus monitoring (economic & physical)',
      'Sales tax chat & advisor answers from verified state sources',
      'Alerts when you cross nexus in new states',
      'Integrations: CSV, QuickBooks, Shopify',
    ],
    cta: { label: 'Get started', href: '/get-started' },
    footnote: '',
  },
  {
    id: 'standard',
    title: 'Standard',
    price: '$49',
    cadence: '/month',
    highlights: [
      'Everything in Monitor',
      'Automated tax calculations',
      'Multi-state tax compliance',
      'Transaction tracking',
      'Quarterly compliance health reviews',
      'Priority email support',
    ],
    cta: { label: 'Talk to sales', href: '/contact' },
    footnote: 'Quarterly reviews include escalation support if states raise issues.',
  },
  {
    id: 'pro',
    title: 'Pro',
    price: '$149',
    cadence: '/month',
    highlights: [
      'Everything in Standard',
      'Advanced automation',
      'Custom integrations',
      'Audit protection',
      'Dedicated account management',
      'API access',
    ],
    cta: { label: 'Get a quote', href: '/contact' },
    footnote: '',
  },
];

const extraBadges = [
  { label: 'Sales Tax Advisor', href: '/advisor' },
  { label: 'Monitor', href: '/get-started' },
];

/* Hero sample: a built nexus monitor for a fictional store. Thresholds are the real
   sales-only economic nexus tests for remote sellers (CA, TX $500,000; FL, WA, PA $100,000).
   Sales figures are invented, round, and labelled as a sample. */
type Status = 'over' | 'near' | 'under' | 'home';
const sampleStates: { code: string; name: string; sales: number; threshold: number; status: Status; note: string }[] = [
  { code: 'FL', name: 'Florida', sales: 131500, threshold: 100000, status: 'over', note: 'Register' },
  { code: 'CA', name: 'California', sales: 412000, threshold: 500000, status: 'near', note: '82%' },
  { code: 'WA', name: 'Washington', sales: 91000, threshold: 100000, status: 'near', note: '91%' },
  { code: 'TX', name: 'Texas', sales: 188000, threshold: 500000, status: 'under', note: '38%' },
  { code: 'PA', name: 'Pennsylvania', sales: 54000, threshold: 100000, status: 'under', note: '54%' },
];

/* Tile map of the 50 states and DC: [code, column, row]. */
const tiles: [string, number, number][] = [
  ['AK', 0, 0], ['ME', 10, 0],
  ['VT', 9, 1], ['NH', 10, 1],
  ['WA', 0, 2], ['ID', 1, 2], ['MT', 2, 2], ['ND', 3, 2], ['MN', 4, 2], ['IL', 5, 2], ['WI', 6, 2], ['MI', 7, 2], ['NY', 8, 2], ['RI', 9, 2], ['MA', 10, 2],
  ['OR', 0, 3], ['NV', 1, 3], ['WY', 2, 3], ['SD', 3, 3], ['IA', 4, 3], ['IN', 5, 3], ['OH', 6, 3], ['PA', 7, 3], ['NJ', 8, 3], ['CT', 9, 3],
  ['CA', 0, 4], ['UT', 1, 4], ['CO', 2, 4], ['NE', 3, 4], ['MO', 4, 4], ['KY', 5, 4], ['WV', 6, 4], ['VA', 7, 4], ['MD', 8, 4], ['DE', 9, 4],
  ['AZ', 1, 5], ['NM', 2, 5], ['KS', 3, 5], ['AR', 4, 5], ['TN', 5, 5], ['NC', 6, 5], ['SC', 7, 5], ['DC', 8, 5],
  ['OK', 3, 6], ['LA', 4, 6], ['MS', 5, 6], ['AL', 6, 6], ['GA', 7, 6],
  ['HI', 0, 7], ['TX', 3, 7], ['FL', 8, 7],
];
/* No statewide sales tax (fact): Oregon, Montana, New Hampshire, Delaware. */
const noStateTax = new Set(['OR', 'MT', 'NH', 'DE']);
const homeState = 'OH';

const money = (n: number) => '$' + n.toLocaleString('en-US');

const moments = [
  {
    title: 'Economic nexus, state by state',
    text: 'Sales and transactions are counted against each state\u2019s threshold. You get an alert when you cross one.',
  },
  {
    title: 'The right rate at checkout',
    text: 'Calculations to the zip code, with state, county, city and district rates listed on every order.',
  },
  {
    title: 'Product taxability',
    text: 'Map each product to a tax code, so clothing, food and software follow each state\u2019s rules.',
  },
  {
    title: 'Registration and filings',
    text: 'Register in a state once you cross its threshold, then track each filing period through to paid.',
  },
  {
    title: 'Records for an audit',
    text: 'Transaction history, variance checks and exportable reports, ready when a state writes.',
  },
];

const sources = [
  { name: 'Shopify', how: 'Connect your store', what: 'Orders arrive in real time' },
  { name: 'QuickBooks', how: 'Connect your company', what: 'Sales sync automatically' },
  { name: 'CSV upload', how: 'Export from any platform', what: 'Transactions import in one file' },
];

export const LandingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    // In-page anchors (/#pricing from the navbar, /pricing redirect): the SPA renders after the
    // browser's own hash scroll, so scroll once the section exists.
    useEffect(() => {
        const id = decodeURIComponent((location.hash || '').replace(/^#/, ''));
        if (!id) return undefined;
        let tries = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const go = () => {
            const el = document.getElementById(id);
            if (el) { el.scrollIntoView({ behavior: 'auto', block: 'start' }); return; }
            if (tries++ < 20) timer = setTimeout(go, 100);
        };
        go();
        return () => { if (timer) clearTimeout(timer); };
    }, [location.hash, location.key]);

    // How-To Guide for Getting Started with GoTruTax Tax Compliance
    const gettingStartedSteps = [
        {
            name: "Sign up for a free account",
            text: "Create your GoTruTax account in under 2 minutes. No credit card required for the free tier."
        },
        {
            name: "Connect your sales platforms",
            text: "Integrate with Shopify, QuickBooks, or upload CSV files to automatically import your transaction data."
        },
        {
            name: "Monitor your nexus obligations",
            text: "Our AI automatically tracks your sales activity and alerts you when you establish nexus in new states."
        },
        {
            name: "Act on insights fast",
            text: "Use automated alerts, variance checks, and ready-to-export reports to stay ahead in every jurisdiction."
        }
    ];

    const statusOf = (code: string) => sampleStates.find(s => s.code === code)?.status;

    return (
        <>
            <SEO
                title="GoTruTax - Sales Tax Nexus Monitoring, Calculation & Compliance"
                description="GoTruTax tracks your sales against every state's economic nexus threshold, calculates sales tax at checkout, and keeps audit-ready records. Start free with the Monitor plan."
                keywords={[
                    'sales tax compliance',
                    'tax automation',
                    'multi-state tax',
                    'nexus determination',
                    'ecommerce tax',
                    'sales tax calculator',
                    'tax compliance platform',
                    'sales tax management',
                    'economic nexus',
                    'marketplace facilitator',
                    'remote seller',
                    'sales tax software',
                    'tax compliance automation'
                ]}
                type="website"
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'SoftwareApplication',
                    'name': 'GoTruTax',
                    'description': 'Sales tax software for US sellers: economic nexus monitoring by state, sales tax calculation, product taxability, registration, filings and audit-ready records',
                    'url': 'https://gotrutax.com',
                    'applicationCategory': 'BusinessApplication',
                    'operatingSystem': 'Web Browser',
                    'offers': {
                        '@type': 'Offer',
                        'price': '0',
                        'priceCurrency': 'USD',
                        'description': 'Free Monitor plan; Standard $49/month; Pro $149/month'
                    },
                    'publisher': {
                        '@type': 'Organization',
                        'name': 'GoTruTax',
                        'url': 'https://gotrutax.com'
                    },
                    'featureList': [
                        'Economic Nexus Monitoring',
                        'Sales Tax Calculations',
                        'Multi-State Tax Compliance',
                        'Product Taxability and Tax Codes',
                        'Sales Tax Registration and Filings',
                        'Platform Integrations',
                        'CSV Transaction Import'
                    ],
                    'screenshot': 'https://gotrutax.com/hero-image.png'
                }}
            />

            <HowToSchema
                name="How to Get Started with GoTruTax Tax Compliance"
                description="Complete guide to setting up GoTruTax for automated sales tax compliance and multi-state tax management."
                steps={gettingStartedSteps}
                totalTime="PT10M"
            />

            <div className="gtl" data-theme="furrow">

                {/* Hero: Stage, the product built as a sample nexus monitor */}
                <section className="hero" aria-labelledby="hero-title">
                    <div className="container hero-grid">
                        <div className="hero-copy">
                            <h1 id="hero-title">Know where you owe sales&nbsp;tax.</h1>
                            <p className="lead">GoTruTax measures your sales in every state against its economic nexus threshold.</p>
                            <div className="hero-actions">
                                {user ? (
                                    <>
                                        <button type="button" className="key key-primary" onClick={() => navigate('/dashboard')}>
                                            Go to dashboard
                                        </button>
                                        <button type="button" className="key key-quiet" onClick={() => navigate('/complete-tax-view')}>
                                            View tax insights
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className="key key-primary" onClick={() => navigate('/get-started')}>
                                            Start free
                                        </button>
                                        <button type="button" className="key key-quiet" onClick={() => navigate('/login')}>
                                            Sign in
                                        </button>
                                    </>
                                )}
                            </div>
                            <p className="hero-terms">Monitor, the free plan, alerts you when you cross a state’s threshold.</p>
                        </div>

                        <figure
                            className="hero-visual"
                            data-slot="hero"
                            data-kind="product"
                            data-alt="The GoTruTax nexus monitor for a store selling in several states"
                        >
                            <div className="sheet" aria-hidden="true">
                                <div className="tilemap">
                                    {tiles.map(([code, col, row]) => {
                                        const st = statusOf(code);
                                        const cls = st ? `tile tile-${st}` : code === homeState ? 'tile tile-home' : noStateTax.has(code) ? 'tile tile-none' : 'tile';
                                        return (
                                            <span key={code} className={cls} style={{ gridColumn: col + 1, gridRow: row + 1 }}>{code}</span>
                                        );
                                    })}
                                </div>
                                <ul className="map-key">
                                    <li><i className="sw sw-over" />Over threshold</li>
                                    <li><i className="sw sw-near" />Within 20%</li>
                                    <li><i className="sw sw-under" />Tracking</li>
                                    <li><i className="sw sw-home" />Registered</li>
                                    <li><i className="sw sw-none" />No state sales tax</li>
                                </ul>
                            </div>

                            <div className="monitor" role="group" aria-label="Sample state threshold tracker">
                                <div className="monitor-head">
                                    <span className="monitor-title">State thresholds</span>
                                    <span className="monitor-store">Example Goods Co.</span>
                                </div>
                                <table className="monitor-table">
                                    <caption className="visually-hidden">Sample store sales compared with each state’s economic nexus threshold</caption>
                                    <thead>
                                        <tr><th scope="col">State</th><th scope="col" className="r">Sales</th><th scope="col" className="r">Threshold</th><th scope="col" className="r">Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {sampleStates.map(s => (
                                            <tr key={s.code} className={`row-${s.status}`}>
                                                <th scope="row">
                                                    {s.name}
                                                    <span className="meter" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.round((s.sales / s.threshold) * 100))}%` }} /></span>
                                                </th>
                                                <td className="r num">{money(s.sales)}</td>
                                                <td className="r num">{money(s.threshold)}</td>
                                                <td className="r"><span className={`flag flag-${s.status}`}>{s.note}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="monitor-foot">Sample store. Sales over each state’s measuring period.</p>
                            </div>
                        </figure>
                    </div>
                </section>

                <FeatureBento variant="landing" />

                {/* What it does: Rows */}
                <section className="band-rows" aria-labelledby="does-title">
                    <div className="container rows-grid">
                        <div className="rows-head">
                            <h2 id="does-title">From first sale in a state to a filed return</h2>
                            <p className="rows-aside">
                                New to nexus? <a href="#blogs-knowledge">Read the guides</a> first.
                            </p>
                        </div>
                        <ol className="rows-list">
                            {moments.map((m, i) => (
                                <li key={m.title}>
                                    <span className="rows-n num">{String(i + 1).padStart(2, '0')}</span>
                                    <h3>{m.title}</h3>
                                    <p>{m.text}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* The calculation: Split, a sample order record */}
                <section className="band-calc" aria-labelledby="calc-title">
                    <div className="container calc-grid">
                        <figure className="receipt" data-slot="order" data-kind="product" aria-label="Sample sales tax calculation for one order">
                            <div className="receipt-head">
                                <span>Order 1042</span>
                                <span>Sample order</span>
                            </div>
                            <table className="receipt-table">
                                <tbody>
                                    <tr><th scope="row">Wool socks<span className="code">Clothing</span></th><td className="r num">$40.00</td><td className="r">Taxable</td></tr>
                                    <tr><th scope="row">Gift card<span className="code">Gift card</span></th><td className="r num">$60.00</td><td className="r">Not taxable</td></tr>
                                </tbody>
                            </table>
                            <table className="receipt-table receipt-rates">
                                <tbody>
                                    <tr><th scope="row">State</th><td className="r num">4.000%</td><td className="r num">$1.60</td></tr>
                                    <tr><th scope="row">County</th><td className="r num">1.000%</td><td className="r num">$0.40</td></tr>
                                    <tr><th scope="row">City</th><td className="r num">2.500%</td><td className="r num">$1.00</td></tr>
                                    <tr><th scope="row">Special district</th><td className="r num">0.500%</td><td className="r num">$0.20</td></tr>
                                </tbody>
                                <tfoot>
                                    <tr><th scope="row">Sales tax</th><td className="r num">8.000%</td><td className="r num">$3.20</td></tr>
                                </tfoot>
                            </table>
                            <figcaption>Sample calculation. Real rates depend on the delivery address.</figcaption>
                        </figure>
                        <div className="calc-copy">
                            <h2 id="calc-title">Every rate on the order, line by line</h2>
                            <p className="lead">Each item is taxed by its product code, and each jurisdiction’s rate is shown, so you can see why the total is what it is.</p>
                        </div>
                    </div>
                </section>

                {/* Where orders come from: Index */}
                <section className="band-sources" aria-labelledby="sources-title">
                    <div className="container sources-grid">
                        <h2 id="sources-title">Bring in your orders</h2>
                        <table className="sources">
                            <thead>
                                <tr><th scope="col">Source</th><th scope="col">How</th><th scope="col">What you get</th></tr>
                            </thead>
                            <tbody>
                                {sources.map(s => (
                                    <tr key={s.name}><th scope="row">{s.name}</th><td>{s.how}</td><td>{s.what}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Pricing: Offer, kept verbatim */}
                <section id="pricing" className="band-pricing" aria-labelledby="pricing-title">
                    <div className="container">
                        <div className="pricing-head">
                            <h2 id="pricing-title">Simple &amp; transparent pricing</h2>
                            <p className="pricing-sub">Choose the plan that fits your business. Start free with Monitor, or upgrade to Standard or Pro for advanced features.</p>
                            <p className="pricing-badges">
                                {extraBadges.map(b => (
                                    <a key={b.label} className="tag" href={b.href}>{b.label}</a>
                                ))}
                            </p>
                        </div>
                        <div className="plans">
                            {tiers.map(tier => (
                                <article key={tier.id} className={`plan plan-${tier.id}`} aria-labelledby={`plan-${tier.id}`}>
                                    <h3 id={`plan-${tier.id}`}>{tier.title}</h3>
                                    <p className="plan-price">
                                        <span className="num">{tier.price}</span>
                                        {tier.cadence && <span className="plan-cadence">{tier.cadence}</span>}
                                    </p>
                                    <ul className="plan-list">
                                        {tier.highlights.map(h => <li key={h}>{h}</li>)}
                                    </ul>
                                    {tier.footnote && <p className="plan-note">{tier.footnote}</p>}
                                    <a className="key key-quiet plan-cta" href={tier.cta.href}>{tier.cta.label}</a>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Blogs & Knowledge with the newsletter: Split */}
                <section id="blogs-knowledge" className="band-know" aria-labelledby="know-title">
                    <div className="container know-grid">
                        <div className="know-index">
                            <h2 id="know-title">Blogs &amp; Knowledge</h2>
                            <p className="know-sub">Sales tax guides on nexus, taxability and audits.</p>
                            <ul className="articles">
                                {blogSnippets.map(article => (
                                    <li key={article.id}>
                                        <a
                                            href={`/resources/blogs-knowledge#${article.id}`}
                                            onClick={() => window.location.href = `/resources/blogs-knowledge#${article.id}`}
                                        >
                                            <h3>{article.title}</h3>
                                            <p>{article.snippet}</p>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <aside className="newsletter" aria-labelledby="nl-title">
                            <h3 id="nl-title">Weekly Sales Tax Newsletter</h3>
                            <p>Curated state revenue moves, deadlines, and next steps. We send the latest digest instantly, no sign-in required.</p>
                            <NewsletterCapture source="landing_page_hero" variant="plain" />
                        </aside>
                    </div>
                </section>

                {/* Closing: Band with the three real setup steps */}
                <section className="band-close" aria-labelledby="close-title">
                    <div className="container">
                        <div className="close-grid">
                            <h2 id="close-title">Find out which states you owe.</h2>
                            <a className="key key-primary" href="/get-started">Sign up free</a>
                        </div>
                        <ol className="close-steps">
                            {gettingStartedSteps.slice(0, 3).map((step, i) => (
                                <li key={step.name}>
                                    <span className="num">{i + 1}</span>
                                    {step.name}
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            </div>
        </>
    );
};
